import Reveal from '@/components/base/Reveal';

const states = [
  {
    tag: 'TRENDING',
    icon: 'ri-line-chart-line',
    lead: 'Direction is established.',
    body: 'Price is travelling one way — printing higher highs and higher lows, or lower highs and lower lows. In this state the system looks for trades that go with the trend, entering on a pullback rather than at the top of a move that has already run.',
  },
  {
    tag: 'RANGING',
    icon: 'ri-stack-line',
    lead: 'Price is contained.',
    body: 'Gold is held between a ceiling and a floor with no clear direction. The system does not trade the middle of the range — it waits for price to reach an edge and for the reaction to reveal whether the range will hold or break.',
  },
];

function MarketRead() {
  return (
    <section
      id="market-read"
      className="w-full scroll-mt-[124px] border-y border-background-200 bg-background-100 px-4 md:px-8 lg:px-12 py-16 md:py-24"
    >
      <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="text-[11px] font-data tracking-[0.24em] text-primary-600">
              STRATEGY
            </span>
            <span className="w-8 h-px bg-primary-400"></span>
            <span className="text-[11px] font-data tracking-[0.2em] text-foreground-400">
              READING THE MARKET
            </span>
          </div>
          <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[32px] leading-[1.08] sm:text-[42px] lg:text-[46px]">
            How The Strategy Reads Gold.
          </h2>
          <p className="mt-5 text-[15px] md:text-base leading-relaxed text-foreground-600 max-w-[540px]">
            Before it considers a single trade, the system answers one question: what is gold doing
            right now — trending, or moving sideways? The entire decision chain is built on that
            read.
          </p>
          <p className="mt-4 text-[14px] leading-relaxed text-foreground-500 max-w-[540px]">
            It looks at the shape price forms over time — the sequence of highs and lows — to decide
            whether a real structure exists. A single candle, a spike or a headline is not a
            structure, and it is never treated as one.
          </p>
        </Reveal>

        <Reveal delay={140}>
          <div className="rounded-lg border border-background-300 bg-background-50 overflow-hidden">
            <div className="flex items-center justify-between px-5 h-12 border-b border-background-200 bg-background-100">
              <span className="text-[12px] font-data tracking-widest text-foreground-700">
                MARKET READ
              </span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 breathe"></span>
                <span className="text-[10px] font-data tracking-widest text-primary-600">
                  TWO STATES
                </span>
              </span>
            </div>

            <div className="divide-y divide-background-200">
              {states.map((state) => (
                <div key={state.tag} className="px-5 py-5">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className={`${state.icon} text-[17px] text-primary-700`}></i>
                    </span>
                    <div>
                      <p className="font-data text-[12px] tracking-[0.2em] text-foreground-950">
                        {state.tag}
                      </p>
                      <p className="text-[12px] text-foreground-500">{state.lead}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-[13px] leading-relaxed text-foreground-600">
                    {state.body}
                  </p>
                </div>
              ))}
            </div>

            <div className="px-5 py-3.5 border-t border-background-200 bg-background-100">
              <p className="text-[11px] font-data tracking-wide text-foreground-500">
                Bias comes from structure — not from a candle, a headline or a single indicator
                reading.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default MarketRead;